import { TProduct } from 'widgets/product/ProductDetail';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TInputStepperInstance } from 'widgets/forms/InputStepper';
import {
    TAvailabilityModel,
    TAvailabilityMessages,
    TInStockAvailability,
    TNotAvailable,
    TBackOrderAvailability,
    TPreorderAvailability
} from 'types/models/product';

/**
 * @description Base ProductAvailabilityMixin implementation
 * <br>This class is not intended to have a separate DOM representation.
 * @param ProductDetail Base widget for extending
 * @returns Product availability mixin widget
 */
function ProductAvailabilityMixinClassCreator(ProductDetail: import('widgets/product/ProductDetail').TProductDetail) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductAvailabilityMixin
     * @augments ProductDetail
     * @classdesc Represents product availability component with the next features:
     * 1. Gets and calculates availability model
     * 2. Gets and calculates quantity stepper attributes
     *
     * The main goal of this mixin is to avoid server requests on every quantity change. Initially gets AvailabilityLevels for maximum allowed quantity.
     * On quantity change or variation change recalculates availability status for current product quantity and renders availability component on PDP with correct status and message.
     * Also, it updates the attribute for quantity stepper.
     * @property {string} data-low-stock-class - Class using in case product low stock
     * @property {string} data-in-stock-class - Class using in case product in stock
     * @example <caption>Example HTML structure for ProductAvailabilityMixin</caption>
     * <div
     *      data-ref="availabilityData"
     *      hidden
     *      data-availability-model="${JSON.stringify(pdict.product.availability)}"
     *      data-ready-to-order="${pdict.product.readyToOrder}"
     *      data-is-inaccessible-variant="${pdict.product.isInaccessibleVariant}"
     *      data-product-available="${pdict.product.available}"
     *      data-msgs='{
     *          "reachedLimitError": "<isprint value="${Resource.msg('availability.isreachedlimit.error', 'cart', null)}" encoding="on" />",
     *          "singleInStockItemMsg": "${Resource.msg('availability.isreachedlimit.msg.single', 'cart', null)}",
     *          "multipleInStockItemsMsg": "${Resource.msg('availability.isreachedlimit.msg.multiple', 'cart', null)}",
     *          "backOrderMessage": "${Resource.msg('label.back.order', 'common', null)}",
     *          "backOrderItemsMessage": "${Resource.msg('availability.back.order.items', 'cart', null)}",
     *          "preOrderMessage": "${Resource.msg('label.preorder', 'common', null)}",
     *          "preOrderItemsMessage": "${Resource.msg('availability.pre.order.items', 'cart', null)}",
     *          "lowStockMessage": "${Resource.msg('availability.lowStock', 'product', null)}",
     *          "inStockMessage": "${Resource.msg('label.instock', 'common', null)}",
     *          "outOfStockMessage": "${Resource.msg('availability.outofstock.msg', 'cart', null)}"
     *      }'
     * ></div>
     */
    class ProductAvailabilityMixin extends ProductDetail {
        quantityWidgetId = 'quantity';

        product = {
            ...this.product || {},
            availability: {} as TAvailabilityModel,
            readyToOrder: false,
            isInaccessibleVariant: false,
            productAvailable: false
        };

        init() {
            super.init();
            this.product = {
                ...this.product || {},
                availability: this.ref('availabilityData').data<TAvailabilityModel>('availabilityModel'),
                readyToOrder: this.ref('availabilityData').data<boolean>('readyToOrder'),
                isInaccessibleVariant: this.ref('availabilityData').data<boolean>('isInaccessibleVariant'),
                productAvailable: this.ref('availabilityData').data<boolean>('productAvailable')
            };
        }

        prefs() {
            return {
                lowStockClass: 'm-lowinstock',
                inStockClass: 'm-instock',
                ...super.prefs()
            };
        }

        /**
         * @description Get in stock availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns In stock availability
         */
        getInStockAvailability(availabilityModel: TAvailabilityModel, productQuantity: number): TInStockAvailability {
            const result = {} as TInStockAvailability;

            if (!availabilityModel || !productQuantity) { return result; }

            const inStockQuantity = availabilityModel.availabilityLevelsForMaxAllowedQty.inStock || 0;
            const quantityAvailableToSell = availabilityModel.quantityAvailableToSell || 0;
            const lowStockThreshold = availabilityModel.lowStockThreshold || 0;
            const maxQtyAllowed = availabilityModel.maxQtyAllowed;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');

            if (inStockQuantity === 0) { return result; }

            result.stock = inStockQuantity;

            if (quantityAvailableToSell
                && productQuantity <= quantityAvailableToSell
                && lowStockThreshold > 0
                && quantityAvailableToSell < lowStockThreshold
            ) {
                result.isLowStock = true;
                result.class = this.prefs().lowStockClass;
                result.lowStockMessage = availabilityMessages?.lowStockMessage;
            } else if (inStockQuantity >= productQuantity
                && productQuantity <= maxQtyAllowed) {
                result.isInStock = true;
                result.class = this.prefs().inStockClass;
                result.inStockMessage = availabilityMessages?.inStockMessage;
            } else {
                result.class = this.prefs().lowStockClass;

                if (inStockQuantity < productQuantity
                    || productQuantity > maxQtyAllowed) {
                    result.isReachedLimit = true;
                    result.isMaxQtyAllowedReached = productQuantity >= maxQtyAllowed;
                    result.isReachedLimitMessage = availabilityMessages?.reachedLimitError;
                    result.inStockMsg = this.getInStockMessage(inStockQuantity, availabilityMessages);
                }
            }

            return result;
        }

        /**
         * @description Get non available availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns Non available availability data
         */
        getNotAvailable(availabilityModel: TAvailabilityModel, productQuantity: number): TNotAvailable {
            const notAvailable = availabilityModel.availabilityLevelsForMaxAllowedQty.notAvailable;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');
            const notAvailableQuantity = notAvailable >= productQuantity ? productQuantity : notAvailable;
            const result = {} as TNotAvailable;

            if (notAvailableQuantity > 0) {
                if (notAvailableQuantity === productQuantity
                    && availabilityModel.availabilityLevelsForMaxAllowedQty.inStock === 0
                    && availabilityModel.availabilityLevelsForMaxAllowedQty.backOrder === 0
                    && availabilityModel.availabilityLevelsForMaxAllowedQty.preOrder === 0) {
                    result.class = 'm-outofstock';
                    result.outOfStockMessage = availabilityMessages?.outOfStockMessage;
                    result.isOutOfStock = true;
                }
            }

            return result;
        }

        /**
         * @description Get backorder availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns Backorder availability data
         */
        getBackOrderAvailability(availabilityModel: TAvailabilityModel, productQuantity: number): TBackOrderAvailability {
            const backOrder = availabilityModel.availabilityLevelsForMaxAllowedQty.backOrder;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');
            const backOrderQuantity = backOrder >= productQuantity ? productQuantity : backOrder;
            const result = {} as TBackOrderAvailability;

            if (backOrderQuantity > 0
                && availabilityModel.availabilityLevelsForMaxAllowedQty.inStock === 0
                && availabilityModel.availabilityLevelsForMaxAllowedQty.preOrder === 0
            ) {
                result.isBackOrder = true;

                if (productQuantity <= backOrderQuantity) {
                    result.class = this.prefs().inStockClass;

                    if (availabilityModel && availabilityModel.inStockDate && availabilityModel.messages.length >= 0) {
                        result.backOrderMessage = availabilityModel.messages[0];
                    } else {
                        result.backOrderMessage = availabilityMessages?.backOrderMessage;
                    }
                } else {
                    result.class = this.prefs().lowStockClass;
                    result.isReachedLimit = true;
                    result.isReachedLimitMessage = availabilityMessages?.reachedLimitError;
                    result.backOrderMessage = availabilityMessages?.backOrderItemsMessage?.replace('#', String(productQuantity));
                }
            }

            return result;
        }

        /**
         * @description Get preorder availability data
         * @param availabilityModel Product availability model
         * @param productQuantity Selected product quantity
         * @returns Preorder availability data
         */
        getPreorderAvailability(availabilityModel: TAvailabilityModel, productQuantity: number): TPreorderAvailability {
            const preOrder = availabilityModel.availabilityLevelsForMaxAllowedQty.preOrder;
            const availabilityMessages = this.ref('availabilityData').data<TAvailabilityMessages>('msgs');
            const preOrderQuantity = preOrder >= productQuantity ? productQuantity : preOrder;
            const result = {} as TPreorderAvailability;

            if (preOrderQuantity > 0
                && availabilityModel.availabilityLevelsForMaxAllowedQty.inStock === 0
                && availabilityModel.availabilityLevelsForMaxAllowedQty.backOrder === 0
            ) {
                result.isPreOrder = true;

                if (productQuantity <= preOrderQuantity) {
                    result.class = this.prefs().inStockClass;

                    if (availabilityModel && availabilityModel.inStockDate && availabilityModel.messages.length >= 0) {
                        result.preOrderMessage = availabilityModel.messages[0];
                    } else {
                        result.preOrderMessage = availabilityMessages?.preOrderMessage;
                    }
                } else {
                    result.class = this.prefs().lowStockClass;
                    result.isReachedLimit = true;
                    result.isReachedLimitMessage = availabilityMessages?.reachedLimitError;
                    result.preOrderMessage = availabilityMessages?.preOrderItemsMessage?.replace('#', String(productQuantity));
                }
            }

            return result;
        }

        /**
         * @description Get correct in stock message
         * @param inStockQuantity Product in stock quantity
         * @param availabilityMessages Availability messages
         * @returns Correct in stock message
         */
        getInStockMessage(inStockQuantity: number, availabilityMessages: TAvailabilityMessages | undefined): string {
            let inStockMessage;

            if (inStockQuantity > 1) {
                inStockMessage = availabilityMessages?.multipleInStockItemsMsg?.replace('#', String(inStockQuantity));
            } else {
                inStockMessage = availabilityMessages?.singleInStockItemMsg?.replace('#', String(inStockQuantity));
            }

            return inStockMessage || '';
        }

        /**
         * @description Get quantity stepper attributes for current product quantity
         * @param availabilityModel Product availability model
         * @param minOrderQuantity Minimum product order quantity
         * @param stepQuantity Quantity stepper step
         * @returns Quantity stepper attributes
         */
        getQuantityStepperAttributes(availabilityModel: TAvailabilityModel, minOrderQuantity: number, stepQuantity: number) {
            const quantityAvailableToSell = availabilityModel ? availabilityModel.quantityAvailableToSell : null;
            const maxQtyAllowed = availabilityModel.maxQtyAllowed;
            const selectedQty = this.getSelectedQuantity();
            const minOrderQty = minOrderQuantity;
            const stepQty = stepQuantity;
            const currentQty = selectedQty && (selectedQty > minOrderQty) ? selectedQty : minOrderQty;
            const max = availabilityModel
                && (quantityAvailableToSell && quantityAvailableToSell < maxQtyAllowed)
                ? quantityAvailableToSell + 1 : maxQtyAllowed;

            return {
                max: max.toFixed(),
                min: minOrderQty.toFixed(),
                step: stepQty.toFixed(),
                currentQty: currentQty.toFixed()
            };
        }

        /**
         * @description Get selected product quantity
         * @returns Selected product quantity
         */
        getSelectedQuantity(): number {
            return this.getById(
                this.quantityWidgetId,
                (quantityStepper: TInputStepperInstance) => parseInt(quantityStepper.getValue(), 10)
            ) || this.prefs().selectedQuantity;
        }

        /**
         * @description Updates availability preferences
         * @param availabilityModel Product availability model
         * @param selectedQuantity selected quantity
         */
        updateAvailabilityPreferences(availabilityModel: TAvailabilityModel, selectedQuantity) {
            this.setPref('selectedQuantity', selectedQuantity);

            this.product.stock = availabilityModel?.availabilityLevelsForMaxAllowedQty?.inStock ?? null;
            this.product.selectedQuantity = selectedQuantity;
        }

        /**
         * @description Change quantity handler
         * @param quantityStepper Quantity stepper widget
         */
        changeQuantity(quantityStepper) {
            super.changeQuantity(quantityStepper);

            const availabilityModel = this.product.availability as TAvailabilityModel;
            const readyToOrder = this.product.readyToOrder;
            const isInaccessibleVariant = this.product.isInaccessibleVariant;
            const productAvailable = this.product.productAvailable;

            if (!availabilityModel || !readyToOrder) { return Promise.resolve(null); }

            this.product = this.reCalculateAvailabilityModel(this.product);

            this.updateAvailabilityPreferences(this.product.availability, this.getSelectedQuantity());

            return this.renderAvailability(
                {
                    available: productAvailable,
                    isInaccessibleVariant: isInaccessibleVariant
                } as TProduct
            )
                ?.then(() => {
                    this.callIfExists('onQuantityUpdate', {
                        availability: this.product.availability,
                        available: productAvailable,
                        selectedQuantity: this.product.selectedQuantity
                    });
                })
                .finally(() => {
                    this.eventBus().emit('alert.show', {
                        accessibilityAlert: this.prefs().accessibilityAlerts.quantitychanged
                    });
                });
        }

        /**
         * @description Update widget preferences
         * @param product Product object
         */
        updatePreferences(product) {
            super.updatePreferences(product);
            this.updateAvailabilityPreferences(product.availability, product.selectedQuantity);
        }

        /**
         * @description Update Product View
         * @param product productModel
         */
        updateProductView(product: TProduct): void {
            product = this.reCalculateAvailabilityModel(product);

            super.updateProductView(product);
            this.renderQuantities(product);
            this.renderAvailability(product);
        }

        /**
         * @description Render product quantity
         * @param product Product object
         */
        renderQuantities(product: TProduct): void {
            const availability = product.availability as TAvailabilityModel;
            const response = product.quantities;

            if (!response || !availability) { return; }

            const quantityStepperAttributes = this.getQuantityStepperAttributes(
                availability,
                product.minOrderQuantity,
                product.stepQuantity
            );

            this.getById<TBasicInputInstance, void>('quantity', (quantity) => {
                quantity.render(
                    'template',
                    {
                        attr: quantityStepperAttributes,
                        isOutOfStock: (availability && availability.isOutOfStock) || false
                    },
                    quantity.ref('container')
                ).then(function () {
                    quantity.update();
                });
            });
        }

        /**
         * @description Recalculate and update availability model before rendering
         * @param product Product object
         */
        reCalculateAvailabilityModel(product: TProduct): TProduct {
            const productQuantity = this.getSelectedQuantity();

            const availabilityModel = product.availability as TAvailabilityModel;

            const availability = {
                ...this.getInStockAvailability(availabilityModel, productQuantity),
                ...this.getNotAvailable(availabilityModel, productQuantity),
                ...this.getBackOrderAvailability(availabilityModel, productQuantity),
                ...this.getPreorderAvailability(availabilityModel, productQuantity),
                availabilityLevelsForMaxAllowedQty: availabilityModel.availabilityLevelsForMaxAllowedQty,
                quantityAvailableToSell: availabilityModel.quantityAvailableToSell,
                lowStockThreshold: availabilityModel.lowStockThreshold,
                maxQtyAllowed: availabilityModel.maxQtyAllowed,
                inStockDate: availabilityModel.inStockDate,
                hasAvailableVariant: availabilityModel.hasAvailableVariant,
                // preserve server-set coming-soon / manual inventory state fields
                isComingSoon: availabilityModel.isComingSoon,
                manualInventoryState: availabilityModel.manualInventoryState,
                manualButtonText: availabilityModel.manualButtonText,
                targetPublishDate: availabilityModel.targetPublishDate
            };

            product.availability = availability;

            return product;
        }

        /**
         * @description Render product availability
         * @param product Product object
         */
        renderAvailability(product: TProduct) {
            if (!this.product.readyToOrder && !this.product.isInaccessibleVariant && !this.product.isMultipackBundle) {
                return Promise.resolve();
            }

            this.has('productAvailabilityMsg', productAvailabilityMsg => {
                productAvailabilityMsg.hide();
            });

            return this.getById('availability', (availabilityLabel) => {
                return availabilityLabel.render(
                    'template',
                    {
                        availability: this.product.availability,
                        productAvailable: product.available
                    },
                    availabilityLabel.ref('container')
                );
            });
        }
    }

    return ProductAvailabilityMixin;
}

export type TProductAvailabilityMixin = ReturnType<typeof ProductAvailabilityMixinClassCreator>;

export type TProductAvailabilityMixinInstance = InstanceType<TProductAvailabilityMixin>;

export default ProductAvailabilityMixinClassCreator;
